import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Phone, 
  RefreshCw, 
  ChevronDown, 
  ChevronUp,
  Target,
  MessageSquare,
  HelpCircle,
  AlertTriangle,
  ArrowRight,
  Copy,
  Check,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';

export default function CallClarityBrief({ opportunityId }) {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [copied, setCopied] = useState(null);
  const api = useApi();

  const generateBrief = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const data = await api.get(
        `/sales-intelligence/call-brief/${opportunityId}?force_refresh=${forceRefresh}`
      );
      
      if (data.status === 'generating') {
        toast.info('Brief is being generated, please wait...');
        return;
      }
      
      if (data.brief?.error) {
        toast.error(data.brief.error);
        return;
      }
      
      setBrief(data);
      if (data.cached) {
        toast.success('Loaded cached brief');
      } else {
        toast.success('Call Clarity Brief generated');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to generate brief');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, section) => {
    navigator.clipboard.writeText(text);
    setCopied(section);
    setTimeout(() => setCopied(null), 2000);
    toast.success('Copied to clipboard');
  };

  const copyAllTalkingPoints = () => {
    if (!brief?.brief) return;
    const points = [
      ...(brief.brief.talking_points || []),
      ...(brief.brief.conversation_guide || [])
    ].join('\n• ');
    copyToClipboard(`• ${points}`, 'all');
  };

  if (!brief) {
    return (
      <Card className="bg-white/80 backdrop-blur border-[#E8E6E1]">
        <CardContent className="p-6">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#B89B5E]/10 flex items-center justify-center">
              <Phone className="h-8 w-8 text-[#B89B5E]" />
            </div>
            <div>
              <h3 className="font-medium text-[#1A1A1A]">AI Call Clarity Brief</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Generate strategic call preparation insights powered by AI
              </p>
            </div>
            <Button 
              onClick={() => generateBrief()} 
              disabled={loading}
              className="bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white"
              data-testid="generate-call-brief-btn"
            >
              {loading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              Generate Call Clarity Brief
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const b = brief.brief;

  return (
    <Card className="bg-white/80 backdrop-blur border-[#E8E6E1]" data-testid="call-clarity-brief">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#B89B5E]/10">
              <Phone className="h-5 w-5 text-[#B89B5E]" />
            </div>
            <div>
              <CardTitle className="text-lg">Call Clarity Brief</CardTitle>
              <p className="text-xs text-muted-foreground">
                {brief.cached ? 'Cached' : 'Generated'} {new Date(brief.generated_at).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={copyAllTalkingPoints}
              className="text-xs"
            >
              {copied === 'all' ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
              Copy Points
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => generateBrief(true)}
              disabled={loading}
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      
      {expanded && (
        <CardContent className="space-y-5">
          {/* Opportunity Context */}
          <div className="p-4 rounded-lg bg-[#FAF9F7] border border-[#E8E6E1]">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-[#B89B5E]" />
              <span className="text-sm font-medium">Opportunity Context</span>
            </div>
            <p className="text-sm text-[#1A1A1A]/80">{b.opportunity_context}</p>
          </div>

          {/* Decision Maker Perspective */}
          <div className="p-4 rounded-lg bg-[#FAF9F7] border border-[#E8E6E1]">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="h-4 w-4 text-[#B89B5E]" />
              <span className="text-sm font-medium">Decision Maker Perspective</span>
            </div>
            <p className="text-sm text-[#1A1A1A]/80">{b.decision_maker_perspective}</p>
          </div>

          {/* Opening Angle */}
          <div className="p-4 rounded-lg bg-gradient-to-r from-[#B89B5E]/10 to-transparent border border-[#B89B5E]/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-[#B89B5E]">Suggested Opening</span>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 px-2"
                onClick={() => copyToClipboard(b.opening_angle, 'opening')}
              >
                {copied === 'opening' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </Button>
            </div>
            <p className="text-sm text-[#1A1A1A] italic">"{b.opening_angle}"</p>
          </div>

          {/* Key Talking Points */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="outline" className="text-[#B89B5E] border-[#B89B5E]/30">
                Key Talking Points
              </Badge>
            </div>
            <ul className="space-y-2">
              {b.talking_points?.map((point, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <ArrowRight className="h-4 w-4 text-[#B89B5E] mt-0.5 flex-shrink-0" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Strategic Questions */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <HelpCircle className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium">Strategic Discovery Questions</span>
            </div>
            <ul className="space-y-2">
              {b.strategic_questions?.map((question, i) => (
                <li key={i} className="flex items-start gap-2 text-sm p-2 rounded bg-blue-50/50">
                  <span className="text-blue-500 font-medium">{i + 1}.</span>
                  <span>{question}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Likely Objections & Responses */}
          {b.likely_objections?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <span className="text-sm font-medium">Objection Handling</span>
              </div>
              <div className="space-y-3">
                {b.likely_objections?.map((objection, i) => (
                  <div key={i} className="p-3 rounded-lg bg-amber-50/50 border border-amber-100">
                    <p className="text-sm font-medium text-amber-700 mb-1">
                      Objection: {objection}
                    </p>
                    {b.response_guidance?.[objection] && (
                      <p className="text-sm text-[#1A1A1A]/80">
                        Response: {b.response_guidance[objection]}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Conversation Guide */}
          {b.conversation_guide?.length > 0 && (
            <div className="p-4 rounded-lg bg-[#1A1A1A] text-white">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-[#B89B5E]">Quick Conversation Guide</span>
              </div>
              <ul className="space-y-2">
                {b.conversation_guide?.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="text-[#B89B5E]">•</span>
                    <span className="text-white/90">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Best Next Step */}
          <div className="p-4 rounded-lg bg-gradient-to-r from-emerald-500/10 to-transparent border border-emerald-200">
            <div className="flex items-center gap-2 mb-2">
              <ArrowRight className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">Recommended Next Step</span>
            </div>
            <p className="text-sm text-[#1A1A1A]">{b.best_next_step}</p>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
